import React, { useCallback, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { FilterConfig } from './DynamicFilters'; // Import DynamicFilters and its type
import { DialogActions, IconButton, TextField, styled } from '@mui/material';
import { Button } from 'components/index';
import AddIcon from '@mui/icons-material/Add';
import { Menu, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomFilters, { CustomFiltersRef } from './CustomFilters';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: '800px',
    maxWidth: 'none',
    maxHeight: '100vh',
    backgroundColor: '#f5f5f5', // Light background
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)', // Subtle shadow
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  borderBottom: '1px solid #e0e0e0', // Divider line
  padding: '16px 24px', // Spacing
  fontWeight: 'bold', // Title font weight
});

const StyledDialogActions = styled(DialogActions)({
  // borderTop: '1px solid #e0e0e0', // Divider line
  padding: '16px 24px', // Spacing
});

interface FilterPanelProps {
  open: boolean;
  onClose: () => void;
  filtersConfig: FilterConfig[];
  onFilterChange: (filterString: any[] | '', rawFilters: any) => void;
  prevAppliedFilters: string | undefined;
}

const FilterPanel: React.FC<FilterPanelProps> = ({ open, onClose, filtersConfig, onFilterChange, prevAppliedFilters }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const customFiltersRef = useRef<CustomFiltersRef>(null);
  const [filterSearch, setFilterSearch] = useState('');

  // Initialize temporary states when dialog opens
  useEffect(() => {
    if (open) {
      if (customFiltersRef.current) customFiltersRef.current.populateTempValues();
    }
  }, [open]);

  const handleMenuClick = (event: any) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleFilterSearchChange = (event: any) => {
    setFilterSearch(event.target.value);
  };

  const handleAddFilterFromMenu = (propertyName: string) => {
    if (customFiltersRef.current) customFiltersRef.current.handleAddFilter(propertyName);
    handleMenuClose();
  };

  const handleKeyDown = (event: any) => {
    event.stopPropagation();
  };

  // Apply filters and update main states
  const applyFilters = useCallback(
    (closeDialog = true) => {
      if (customFiltersRef.current) customFiltersRef.current.applyCurrentFilters();
      if (closeDialog) {
        onClose();
      }
    },
    [onClose],
  );

  const clearFilters = () => {
    if (customFiltersRef.current) customFiltersRef.current.clearFilters();
  };

  const filtersNotOnDisplay = (customFiltersRef.current && customFiltersRef.current.filtersNotOnDisplay) ?? [];
  const searchedFilters = filtersNotOnDisplay.filter(
    config =>
      config.propertyDisplayName.toLowerCase().includes(filterSearch.toLowerCase()) ||
      config.group?.toLowerCase().includes(filterSearch.toLowerCase()),
  );

  return (
    <StyledDialog open={open} onClose={onClose} sx={{ '& .MuiDialog-paper': { minHeight: '700px', maxHeight: '70%' } }} keepMounted>
      <StyledDialogTitle>
        Filters
        <IconButton aria-label='close' onClick={onClose} style={{ position: 'absolute', right: '10px', top: '10px' }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <CustomFilters
        ref={customFiltersRef}
        filtersConfig={filtersConfig}
        onFilterChange={onFilterChange}
        prevAppliedFilters={prevAppliedFilters}
      />
      <StyledDialogActions>
        <Button
          variant='text'
          aria-controls='filter-menu'
          aria-haspopup='true'
          onClick={handleMenuClick}
          style={{ position: 'absolute', left: '10px', bottom: '10px' }}
        >
          <AddIcon /> Add Filters
        </Button>
        <Menu
          id='filter-menu'
          anchorEl={menuAnchorEl}
          keepMounted
          open={isMenuOpen}
          onClose={handleMenuClose}
          PaperProps={{
            style: {
              maxHeight: '300px',
              width: '250px',
              overflow: 'auto',
            },
          }}
        >
          <TextField
            label='Search Filters'
            variant='outlined'
            fullWidth
            margin='dense'
            value={filterSearch}
            onChange={handleFilterSearchChange}
            onKeyDown={handleKeyDown}
          />
          {searchedFilters.map(config => (
            <MenuItem key={config.propertyName} onClick={() => handleAddFilterFromMenu(config.propertyName)}>
              {`${config.group ? `${config.group} - ` : ''}${config.propertyDisplayName}`}
            </MenuItem>
          ))}
        </Menu>
        <Button variant='outlined' color='secondary' onClick={clearFilters}>
          Reset Filters
        </Button>
        <Button variant='contained' color='primary' onClick={applyFilters}>
          Apply Filters
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default FilterPanel;
